import Decimal from 'decimal.js';

import {
  CashflowEndType,
  CashflowFrequency,
  CashflowIncomeOrExpense,
  CashflowValueType,
} from '@/modules/cashflows/CashflowDrawer/CashflowDrawer.types';
import { mapFormDataToCreatePayload } from '@/modules/cashflows/CashflowDrawer/CashflowDrawer.utils';
import {
  AugmentedCreateCashflowInput,
  IntegrationCashflowExternalKind,
} from '@/types/schema';

import {
  CashflowsImportFormShape,
  CashflowsImportRowData,
} from './CashflowsImport.types';
import { CashflowsImportQuery } from './graphql/CashflowsImport.generated';

export type IntegrationCashflowNode = NonNullable<
  NonNullable<
    NonNullable<CashflowsImportQuery['integrationCashflows']['edges']>[number]
  >['node']
>;

function getYear(date: Date | null | undefined): number {
  return date ? date.getUTCFullYear() : new Date().getFullYear();
}

export function toCashflowsImportForm(
  cashflows: IntegrationCashflowNode[]
): CashflowsImportFormShape {
  return {
    cashflows: Object.fromEntries(
      cashflows.map((cashflow) => {
        const startYear = getYear(cashflow.startDate);
        const endYear = getYear(cashflow.endDate ?? cashflow.startDate);

        const row: CashflowsImportRowData = {
          shouldImport: false,
          integrationCashflowId: cashflow.id,
          ownerName: cashflow.ownerName ?? '',
          hasInferredTarget: false,
          name: cashflow.name,
          targetId: '',
          valueType: CashflowValueType.Dollar,
          amount: cashflow.value ? new Decimal(cashflow.value) : null,
          percentage: null,
          growthRate: cashflow.growthRate
            ? new Decimal(cashflow.growthRate)
            : new Decimal(0),
          frequency:
            startYear === endYear
              ? CashflowFrequency.OneTime
              : CashflowFrequency.Annual,
          startYear,
          endType: CashflowEndType.Year,
          endYear,
          endAtDeathClientProfileId: CashflowEndType.BothDeaths,
          incomeOrExpense:
            cashflow.externalKind ===
            IntegrationCashflowExternalKind.EmoneyExpense
              ? CashflowIncomeOrExpense.Expense
              : CashflowIncomeOrExpense.Income,
        };

        return [cashflow.id, row];
      })
    ),
  };
}

export function hasSelectedCashflows(
  cashflows: CashflowsImportFormShape['cashflows']
): boolean {
  return Object.values(cashflows).some((cashflow) => cashflow.shouldImport);
}

export function hasSelectedCashflowWithoutTarget(
  cashflows: CashflowsImportFormShape['cashflows']
): boolean {
  return Object.values(cashflows).some(
    (cashflow) => cashflow.shouldImport && !cashflow.targetId
  );
}

export function mapCashflowsToCreateInputs(
  formData: CashflowsImportFormShape,
  householdId: string
): AugmentedCreateCashflowInput[] {
  return Object.values(formData.cashflows)
    .filter((cashflow) => cashflow.shouldImport)
    .map((cashflow) => {
      const input = mapFormDataToCreatePayload(cashflow, householdId);
      return {
        create: {
          ...input.create,
          integrationCashflowID: cashflow.integrationCashflowId,
        },
      };
    });
}
